import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { makeRequest2 } from '../../../../../../utils/makeRequest';
import { toastify } from '../../../../../../utils/toast';
import { getItem } from '../../../../../../utils/localStorage';
import Layout from '../../../../component/layout';
import { useFacilityStore } from '../../../../../../app/z-store/store';

const AUDIT_URL = '/api/app/power_meter_management/controllers/audit_logs';

function PowerCreditAuditLog() {
    const { accountNumber } = useParams();
    const facilityId = useFacilityStore((state) => state.facilityId);
    const navigate = useNavigate();
    const [allowed, setAllowed] = useState(null);
    const [logs, setLogs] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lazyParams, setLazyParams] = useState({ first: 0, rows: 20 });
    const [reference, setReference] = useState('');
    const [actor, setActor] = useState('');
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        (async () => {
            const appUser = await getItem('APPUSER');
            setAllowed(appUser?.user?.role === 'ninja');
        })();
    }, []);

    const fetchLogs = useCallback(async (params = lazyParams) => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                module: 'power_credit',
                accountNumber,
                facilityId,
                page: String(Math.floor(params.first / params.rows) + 1),
                limit: String(params.rows)
            });
            if (reference.trim()) query.set('reference', reference.trim());
            if (actor.trim()) query.set('actor', actor.trim());

            const response = await makeRequest2(`${AUDIT_URL}?${query.toString()}`, 'GET');
            if (response.success) {
                setLogs(response.data?.data?.logs || []);
                setTotalRecords(response.data?.data?.totalCount || 0);
            } else {
                toastify(response.error || 'Failed to fetch audit logs', 'error');
            }
        } catch (error) {
            toastify('Error fetching audit logs', 'error');
        } finally {
            setLoading(false);
        }
    }, [accountNumber, facilityId, lazyParams, reference, actor]);

    useEffect(() => {
        if (allowed && facilityId) fetchLogs(lazyParams);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allowed, lazyParams, facilityId]);

    const onSearch = () => {
        if (lazyParams.first === 0) fetchLogs({ ...lazyParams, first: 0 });
        else setLazyParams({ ...lazyParams, first: 0 });
    };

    if (allowed === null) return <Layout />;
    if (!allowed) {
        return (
            <Layout>
                <div className="container-fluid pt-4">
                    <div className="alert alert-danger">Access denied.</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="container-fluid">
                <div className="card">
                    <div className="card-header d-flex justify-content-between align-items-center">
                        <h3 className="mb-0">Power Credit Audit Log — Account {accountNumber}</h3>
                        <button className="btn btn-outline-primary" onClick={() => navigate(-1)}>Back</button>
                    </div>
                    <div className="card-body">
                        <div className="row g-2 mb-3">
                            <div className="col-md-4">
                                <input className="form-control" placeholder="Reference" value={reference}
                                    onChange={(e) => setReference(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
                            </div>
                            <div className="col-md-4">
                                <input className="form-control" placeholder="Performed by (name or email)" value={actor}
                                    onChange={(e) => setActor(e.target.value)}
                                    onKeyDown={(e) => e.key === 'Enter' && onSearch()} />
                            </div>
                            <div className="col-md-2">
                                <button className="btn btn-primary w-100" onClick={onSearch}>Search</button>
                            </div>
                        </div>
                        <div className="dt-responsive table-responsive">
                            <DataTable
                                value={logs}
                                lazy
                                paginator
                                first={lazyParams.first}
                                rows={lazyParams.rows}
                                totalRecords={totalRecords}
                                onPage={(e) => setLazyParams({ first: e.first, rows: e.rows })}
                                loading={loading}
                                stripedRows
                                emptyMessage="No audit records found."
                                tableStyle={{ minWidth: '60rem' }}
                            >
                                <Column header="When" body={(r) => new Date(r.createdAt).toLocaleString()} />
                                <Column header="Performed By" body={(r) => (
                                    <div>
                                        <div>{r.actor?.fullName || 'Unknown'}</div>
                                        <small className="text-muted">{r.actor?.email} {r.actor?.role ? `(${r.actor.role})` : ''}</small>
                                    </div>
                                )} />
                                <Column field="action" header="Action" />
                                <Column field="reference" header="Reference" />
                                <Column field="description" header="Description" />
                                <Column header="Status" body={(r) => (
                                    <span className={`badge ${r.status === 'SUCCESS' ? 'bg-success' : 'bg-danger'} text-light`}>{r.status}</span>
                                )} />
                                <Column field="request.ip" header="IP" />
                                <Column header="" body={(r) => (
                                    <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelected(r)}>Details</button>
                                )} />
                            </DataTable>
                        </div>
                    </div>
                </div>
            </div>
            <Dialog header="Audit Details" visible={!!selected} style={{ width: '600px' }} onHide={() => setSelected(null)} modal>
                {selected && <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(selected, null, 2)}</pre>}
            </Dialog>
        </Layout>
    );
}

export default PowerCreditAuditLog;
